import React, { useEffect, useState } from "react";
import { Cookie, ExternalLink, RefreshCw, ScanSearch, Trash2 } from "lucide-react";

const PLATFORM_HOSTS = {
  justforfans: ["justfor.fans", "www.justfor.fans"],
  onlyfans_vip: ["onlyfans.com", "www.onlyfans.com"],
  onlyfans_free: ["onlyfans.com", "www.onlyfans.com"],
  faphouse: ["studio.faphouse.com"],
  theblowers: ["theblowers.com", "www.theblowers.com"],
  rawfuckclub: ["rawfuckclub.com", "www.rawfuckclub.com"],
  intimaly: ["intimaly.com", "www.intimaly.com"],
};

const REQUEST_SOURCE = "GIOPILOS_PLATFORM_HUB";
const RESPONSE_SOURCE = "GIOPILOS_PLATFORM_EXTENSION";

function sendHubRequest(platform, action) {
  return new Promise((resolve, reject) => {
    const requestId = `${Date.now()}-${Math.random().toString(36).slice(2)}`;
    const timeout = window.setTimeout(() => {
      window.removeEventListener("message", onMessage);
      reject(new Error("The GioPilos extension is unavailable or did not respond."));
    }, 1800);
    const onMessage = (event) => {
      if (event.source !== window || event.origin !== window.location.origin
        || event.data?.source !== RESPONSE_SOURCE || event.data.requestId !== requestId) return;
      window.clearTimeout(timeout);
      window.removeEventListener("message", onMessage);
      resolve(event.data.response || {});
    };
    window.addEventListener("message", onMessage);
    window.postMessage({ source: REQUEST_SOURCE, requestId, platform, action }, window.location.origin);
  });
}

function safeProfile(details, platform) {
  if (!details || details.platform !== platform) return null;
  const result = {};
  if (typeof details.handle === "string" && /^[a-z0-9._-]{1,80}$/i.test(details.handle)) result.handle = details.handle;
  if (typeof details.displayName === "string") {
    const displayName = details.displayName.replace(/[\u0000-\u001f\u007f]/g, " ").replace(/\s+/g, " ").trim().slice(0, 80);
    if (displayName) result.displayName = displayName;
  }
  if (typeof details.profileUrl === "string") {
    try {
      const url = new URL(details.profileUrl);
      if (url.protocol === "https:" && PLATFORM_HOSTS[platform]?.includes(url.hostname)) result.profileUrl = `${url.origin}${url.pathname}`;
    } catch (_) {
      // Non-canonical profile URLs are not displayed.
    }
  }
  if ((platform === "onlyfans_vip" && details.accountType === "VIP")
    || (platform === "onlyfans_free" && details.accountType === "Free")) result.accountType = details.accountType;
  return Object.keys(result).length ? result : null;
}

function statusLabel(status) {
  if (status === "valid") return "Present, expiry not reached; login not verified";
  if (status === "expired") return "Expired";
  if (status === "error") return "Error";
  if (status === "none") return "None";
  return "Unknown";
}

function formatUpdatedAt(value) {
  if (!value) return "Never";
  const date = new Date(value);
  return Number.isNaN(date.getTime()) ? "Unavailable" : date.toLocaleString();
}

export default function PlatformHubTabs({ platform, platformName, children, primaryLabel = "Overview" }) {
  const [activeTab, setActiveTab] = useState("overview");
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState("");
  const [error, setError] = useState("");
  const [permissionDenied, setPermissionDenied] = useState(false);
  const [emptyMessage, setEmptyMessage] = useState("");

  const loadStatus = async () => {
    setLoading(true);
    try {
      const response = await sendHubRequest(platform, "status");
      if (response.error) throw new Error(response.error);
      setData(response);
      setError("");
      setPermissionDenied(false);
    } catch (requestError) {
      setError(requestError.message || "Could not load extension status.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { loadStatus(); }, [platform]);

  const runAction = async (action) => {
    if (action === "clear_cookies" && !window.confirm(`Clear browser cookies for ${platformName}? This may sign you out.`)) return;
    setBusy(action);
    setError("");
    setEmptyMessage("");
    setPermissionDenied(false);
    try {
      const response = await sendHubRequest(platform, action);
      if (response.permissionDenied) {
        setPermissionDenied(true);
        return;
      }
      if (response.error) throw new Error(response.error);
      if (response.empty) {
        setEmptyMessage(response.message || "No profile details are available yet.");
        return;
      }
      setData((current) => ({ ...(current || {}), ...response }));
      if (action === "scrape_details") setEmptyMessage("Profile details refreshed.");
    } catch (requestError) {
      setError(requestError.message || "The extension action failed.");
    } finally {
      setBusy("");
    }
  };

  const profile = safeProfile(data?.details, platform);
  const cookieMetadata = Array.isArray(data?.cookieMetadata) ? data.cookieMetadata : [];

  return (
    <div>
      <div role="tablist" aria-label={`${platformName} hub sections`} className="mb-5 flex gap-1 overflow-x-auto border-b border-zinc-800">
        <button type="button" role="tab" aria-selected={activeTab === "overview"}
          className={`min-h-11 shrink-0 border-b-2 px-4 text-sm ${activeTab === "overview" ? "border-emerald-400 text-white" : "border-transparent text-zinc-400 hover:text-white"}`}
          onClick={() => setActiveTab("overview")}>{primaryLabel}</button>
        <button type="button" role="tab" aria-selected={activeTab === "details"}
          className={`min-h-11 shrink-0 border-b-2 px-4 text-sm ${activeTab === "details" ? "border-emerald-400 text-white" : "border-transparent text-zinc-400 hover:text-white"}`}
          onClick={() => setActiveTab("details")}>Details &amp; Cookies</button>
      </div>

      {activeTab === "overview" ? children : (
        <section role="tabpanel" aria-label="Details & Cookies" className="space-y-5">
          <div className="flex flex-wrap items-start justify-between gap-3 border-b border-zinc-800 pb-4">
            <div>
              <h2 className="text-lg font-semibold text-white">{platformName} details &amp; cookies</h2>
              <p className="mt-1 max-w-3xl text-sm text-zinc-400">
                Cookie access is requested only when you choose an action. Values stay in Chrome; the extension stores metadata only.
              </p>
            </div>
            <button type="button" onClick={loadStatus} disabled={loading} aria-label="Refresh platform status"
              className="inline-flex min-h-10 items-center gap-2 border border-zinc-700 px-3 text-sm text-zinc-200 disabled:opacity-50">
              <RefreshCw size={15} className={loading ? "animate-spin" : ""} /> Refresh status
            </button>
          </div>

          {loading && <p role="status" className="py-4 text-sm text-zinc-400">Loading platform status…</p>}
          {!loading && error && <p role="alert" className="border border-red-900 bg-red-950/30 px-3 py-2 text-sm text-red-300">{error}</p>}
          {permissionDenied && <p role="alert" className="border border-amber-800 bg-amber-950/30 px-3 py-2 text-sm text-amber-200">Permission denied. Cookie access remains off; allow the optional Chrome permission and try again.</p>}
          {data?.permissionRequired && !permissionDenied && <p role="status" className="border border-zinc-800 px-3 py-2 text-sm text-zinc-300">Cookie permission is not granted. No cookie metadata has been inspected; choosing a cookie action will request optional access.</p>}
          {data?.loginRequired && <p role="alert" className="border border-amber-800 bg-amber-950/30 px-3 py-2 text-sm text-amber-200">Sign-in is required on the platform page. Cookie expiry metadata cannot confirm a live authenticated session.</p>}
          {emptyMessage && <p role="status" className="border border-zinc-800 px-3 py-2 text-sm text-zinc-300">{emptyMessage}</p>}

          <dl className="grid grid-cols-1 gap-3 border-y border-zinc-800 py-4 sm:grid-cols-3">
            <StatusValue label="Platform connection" value={data?.connectionStatus || "unknown"} />
            <StatusValue label="Cookie-session status" value={statusLabel(data?.cookieSessionStatus)} />
            <StatusValue label="Last updated" value={formatUpdatedAt(data?.lastUpdated)} />
          </dl>

          <div>
            <h3 className="mb-2 text-sm font-semibold text-white">Scraped profile details</h3>
            {!profile ? <p className="text-sm text-zinc-500">No allowlisted profile details saved.</p> : (
              <dl className="grid grid-cols-1 gap-3 sm:grid-cols-2">
                {profile.handle && <StatusValue label="Handle" value={`@${profile.handle}`} />}
                {profile.displayName && <StatusValue label="Display name" value={profile.displayName} />}
                {profile.accountType && <StatusValue label="Account type" value={profile.accountType} />}
                {profile.profileUrl && <div><dt className="text-xs text-zinc-500">Profile</dt><dd className="mt-1"><a href={profile.profileUrl} target="_blank" rel="noreferrer" className="inline-flex items-center gap-1 break-all text-sm text-emerald-300">Open profile <ExternalLink size={13} /></a></dd></div>}
              </dl>
            )}
          </div>

          <div className="overflow-hidden border-y border-zinc-800">
            <div className="flex flex-wrap items-center justify-between gap-2 py-3">
              <h3 className="text-sm font-semibold text-white">Cookie metadata</h3>
              <span className="text-xs text-zinc-500">Values masked</span>
            </div>
            {cookieMetadata.length === 0 ? <p className="border-t border-zinc-800 py-4 text-sm text-zinc-500">No cookie metadata available.</p> : (
              <div className="overflow-x-auto">
                <table className="w-full min-w-[560px] text-left text-xs">
                  <thead className="border-t border-zinc-800 text-zinc-500"><tr><th className="py-2 pr-3">Domain</th><th className="py-2 pr-3">Name category</th><th className="py-2 pr-3">Expiry</th><th className="py-2 pr-3">Status</th><th className="py-2">Value</th></tr></thead>
                  <tbody>{cookieMetadata.map((cookie, index) => <tr key={`${cookie.domain}:${cookie.category}:${index}`} className="border-t border-zinc-900 text-zinc-300">
                    <td className="py-2 pr-3">{cookie.domain}</td><td className="py-2 pr-3">{cookie.category}</td><td className="py-2 pr-3">{formatUpdatedAt(cookie.expiresAt)}</td><td className="py-2 pr-3">{cookie.status}</td><td className="py-2 font-mono">********</td>
                  </tr>)}</tbody>
                </table>
              </div>
            )}
          </div>

          <div className="grid grid-cols-1 gap-2 sm:grid-cols-2 xl:grid-cols-4">
            <ActionButton icon={Cookie} data-platform-hub-action="save_cookies" data-platform-hub-platform={platform} disabled={Boolean(busy)} onClick={() => runAction("save_cookies")}>Save cookies</ActionButton>
            <ActionButton icon={RefreshCw} data-platform-hub-action="refresh_session" data-platform-hub-platform={platform} disabled={Boolean(busy)} onClick={() => runAction("refresh_session")}>{busy === "refresh_session" ? "Refreshing…" : "Refresh session"}</ActionButton>
            <ActionButton icon={Trash2} data-platform-hub-action="clear_cookies" data-platform-hub-platform={platform} disabled={Boolean(busy)} onClick={() => runAction("clear_cookies")}>Clear cookies</ActionButton>
            <ActionButton icon={ScanSearch} data-platform-hub-action="scrape_details" data-platform-hub-platform={platform} disabled={Boolean(busy)} onClick={() => runAction("scrape_details")}>{busy === "scrape_details" ? "Scraping…" : "Scrape / refresh details"}</ActionButton>
          </div>
        </section>
      )}
    </div>
  );
}

function StatusValue({ label, value }) {
  return <div className="min-w-0"><dt className="text-xs text-zinc-500">{label}</dt><dd className="mt-1 break-words text-sm capitalize text-zinc-200">{value}</dd></div>;
}

function ActionButton({ icon: Icon, children, ...props }) {
  return <button type="button" className="inline-flex min-h-11 items-center justify-center gap-2 border border-zinc-700 px-3 text-sm text-zinc-200 transition hover:bg-zinc-900 disabled:cursor-not-allowed disabled:opacity-50" {...props}><Icon size={15} />{children}</button>;
}